/**
 * Batched Storefront GraphQL fetches for entry_multiplier via site.route(path).
 * BigCommerce caps query complexity (~10000); batching avoids a single oversized query.
 */
import { normalizeMultiplier, parseNumber } from './helpers';

/** ~3.4k complexity per route branch observed with 4 products → keep chunks small. */
export const ROUTE_MULTIPLIER_BATCH_SIZE = 2;

function chunkArray(items, size) {
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
}

export function buildRouteMultiplierQuery(paths) {
    const routeQueries = paths.map((path, index) => `
                product${index}: route(path: ${JSON.stringify(path)}) {
                    node {
                        ... on Product {
                            customFields(first: 25) {
                                edges {
                                    node {
                                        name
                                        value
                                    }
                                }
                            }
                        }
                    }
                }`).join('');

    return `query RouteEntryMultipliers {
        site {${routeQueries}
        }
    }`;
}

function parseChunkToMultipliersByPath(siteData, paths) {
    return paths.reduce((result, path, index) => {
        const customFieldEdges = siteData[`product${index}`]?.node?.customFields?.edges || [];
        const entryMultiplierNode = customFieldEdges.find((edge) => edge?.node?.name === 'entry_multiplier');
        result[path] = normalizeMultiplier(parseNumber(entryMultiplierNode?.node?.value));
        return result;
    }, {});
}

async function fetchRouteMultiplierChunk(paths, storefrontApiToken) {
    if (!storefrontApiToken || paths.length === 0) {
        return {};
    }

    const response = await fetch('/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            Authorization: `Bearer ${storefrontApiToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            query: buildRouteMultiplierQuery(paths),
        }),
    });

    let payload;

    try {
        payload = await response.json();
    } catch (e) {
        throw new Error(`GraphQL response parse failed with status ${response.status}`);
    }

    if (!response.ok) {
        const msg = payload?.errors?.[0]?.message || `status ${response.status}`;
        throw new Error(`GraphQL request failed: ${msg}`);
    }

    if (payload.errors?.length && !payload.data?.site) {
        const msg = payload.errors.map((e) => e.message).join('; ');
        throw new Error(msg || 'GraphQL returned errors with no data');
    }

    if (payload.errors?.length) {
        console.warn('[Raffle] Route multiplier GraphQL warnings:', payload.errors);
    }

    const siteData = payload?.data?.site || {};
    return parseChunkToMultipliersByPath(siteData, paths);
}

/**
 * Fetches entry_multiplier for each path, in parallel chunks to stay under API complexity limits.
 *
 * @param {string[]} paths - Product URL paths (e.g. "/foo-bar/")
 * @param {string} storefrontApiToken
 * @param {Object} [options]
 * @param {number} [options.batchSize] - Defaults to ROUTE_MULTIPLIER_BATCH_SIZE
 * @returns {Promise<Record<string, number>>} path -> normalized multiplier
 */
export async function fetchRouteMultipliersBatched(paths, storefrontApiToken, options = {}) {
    if (!storefrontApiToken || paths.length === 0) {
        return {};
    }

    const batchSize = options.batchSize ?? ROUTE_MULTIPLIER_BATCH_SIZE;
    const uniquePaths = Array.from(new Set(paths.filter(Boolean)));
    const chunks = chunkArray(uniquePaths, batchSize);

    const settled = await Promise.allSettled(
        chunks.map((chunk) => fetchRouteMultiplierChunk(chunk, storefrontApiToken)),
    );

    const merged = {};

    settled.forEach((result, index) => {
        if (result.status === 'fulfilled') {
            Object.assign(merged, result.value);
        } else {
            console.warn('[Raffle] Route multiplier chunk failed:', chunks[index], result.reason);
        }
    });

    return merged;
}
